import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output
} from '@angular/core';
import {ObjectHoliday} from '../object-holiday.model';
import {ApiList} from '../../API/api-list.enum';
import {TranslateService} from '@ngx-translate/core';
import {SemRestRequestService} from '../../../../projects/api/src/sem-rest-request/sem-rest-request.service';
import {LocaleService} from '../../shared/services/locale.service';
import {DateTimeAdapter, OwlDateTimeIntl} from '@danielmoncada/angular-datetime-picker';
import {formatNumberToTime, parseIntervalToMinutes} from '../../shared/pipes/time.pipe';
import {ActivatedRoute, Router} from '@angular/router';
import {UserService} from '../../shared/services/user.service';
import {ConfigureService} from '../../../../projects/common/modules/configure/configure.service';
import {InsparkMessageService} from '@inspark/inspark-components';

/**
 * Компонент для загрузки нескольких строк из таблицы ObjectHoliday
 * при получении единственного ID форма работает в режиме редактирования
 * при количестве ID больше 1 форма работает в декларативном стиле
 */
@Component({
  selector: 'app-object-holiday',
  templateUrl: './object-holiday.component.html',
  styleUrls: ['./object-holiday.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObjectHolidayComponent implements OnInit {
  @Input() ids: any;
  @Input() names: any;
  @Input() saveComplete;
  @Input() disableRoute = false;
  @Output() saveDone = new EventEmitter();
  @Output() cancel = new EventEmitter();
  controllerIds = [];
  isRouteToControllers = false;
  isApplyScheduleToController = false;
  public objectHolidays: ObjectHoliday[] = [];
  public showModalDialog = false;
  public modalDialogTemplate;
  public isValid = true;
  isPrivilege = true;
  isAdmin = false;

  constructor(private request: SemRestRequestService,
              private  userService: UserService,
              private translate: TranslateService,
              private owlDateTimeIntl: OwlDateTimeIntl,
              private localeService: LocaleService,
              private dateTimeAdapter: DateTimeAdapter<Date>,
              private cd: ChangeDetectorRef,
              private router: Router,
              private route: ActivatedRoute,
              private config: ConfigureService,
              private messageService: InsparkMessageService
  ) {
    localeService.locale$.subscribe(locale => {
      dateTimeAdapter.setLocale(locale.serverShort);
    });
    this.isAdmin = config.options.appName === 'admin';
    this.addObjectHoliday();
    this.isPrivilege = this.userService.checkAccess('objectholiday:update');
  }

  ngOnInit() {
    this.loadData();
  }

  loadData() {
    if (!this.ids || this.ids.length === 0) {
      return;
    }
    if (this.ids.length === 1) {
      this.request
        .baseUrlAppend(ApiList.Db, ApiList.Object, this.ids[0], ApiList.Objectholiday)
        .getAll<any>().subscribe(data => this.parse(data));
    }

    this.request
      .baseUrlAppend(ApiList.Db, ApiList.Controller, ApiList.Child, ApiList.Object, this.ids[0])
      .getAll<any>().subscribe(data =>  {
      if (data && data.length > 0) {
        this.controllerIds = data.map(controller => controller.id);
        if (!this.isAdmin) {
          this.isApplyScheduleToController = false;
          this.isRouteToControllers = false;
        } else  if (this.controllerIds.length === 1) {
          this.isApplyScheduleToController = true;
          this.isRouteToControllers = false;
        } else if (this.controllerIds.length > 1) {
          this.isRouteToControllers = true;
          this.isApplyScheduleToController = false;
        }

      }
    });
  }

  /**
   * Функция выполняет разбор и мапинг данных, полученных из Rest, на внутренние структуры компонента
   * @param data
   */
  public parse(data) {
    const newObjectHolidays: ObjectHoliday[] = [];
    data.forEach(_objectHoliday => {
      newObjectHolidays.push({
        date: parseInt(_objectHoliday.date, 10),
        operatingmode: _objectHoliday.operatingmode !== null ?
          {
            id: parseInt(_objectHoliday.operatingmode.id, 10),
            weekday: parseInt(_objectHoliday.operatingmode.weekday, 10)
          } :
          {weekday: 1},
        type: (_objectHoliday.operatingmode === null && _objectHoliday.workingstart === null && _objectHoliday.workingstop === null)
        || _objectHoliday.operatingmode && (_objectHoliday.operatingmode.weekday !== undefined)
        && _objectHoliday.operatingmode.weekday >= 0
        && _objectHoliday.operatingmode.weekday < 9 ? 'day' : 'time',
        workingstart: formatNumberToTime(_objectHoliday.workingstart || '0'),
        workingstop: formatNumberToTime(_objectHoliday.workingstop || '0')
      });
    });
    this.objectHolidays = newObjectHolidays;
    this.cd.markForCheck();
  }

  /** Выполняет формирование и вызов REST запросов */
  createObjectHolidays() {
    const objectHolidaysRESTData = [];

    this.objectHolidays.forEach(_objectHoliday => {
      if (!_objectHoliday.type || _objectHoliday.type === 'day') {
        objectHolidaysRESTData.push({
          date: _objectHoliday.date - Number(new Date().getTimezoneOffset() * 60 * 1000),
          weekday: _objectHoliday.weekday !== undefined ? _objectHoliday.weekday : _objectHoliday.operatingmode.weekday !== undefined ? _objectHoliday.operatingmode.weekday : 1,
        });
      } else {
        objectHolidaysRESTData.push({
          date: _objectHoliday.date - Number(new Date().getTimezoneOffset() * 60 * 1000),
          workingstart: parseIntervalToMinutes(_objectHoliday.workingstart + ''),
          workingstop: parseIntervalToMinutes(_objectHoliday.workingstop + '')
        });
      }
    });

    this.request
      .baseUrlAppend(ApiList.Db, ApiList.Object, ApiList.Objectholiday, ApiList.Update)
      .baseUrlAppendByStrParam('?id=' + this.ids.join('&id='))
      .postData(objectHolidaysRESTData)
      .subscribe(() => {
        if (this.isApplyScheduleToController && this.isAdmin) {
          this.request.baseUrlAppend(ApiList.Db, ApiList.Controller, this.controllerIds[0])
            .baseUrlAppendByStrParam('/deploy_schedule')
            .getSingle().subscribe((data) => {
            /* USTAS SEM-6081 - желательно показывать сообщение, что команда выполена на контроллере */
              this.messageService.success('Command excecuted successfully on controller!');
              this.saveComplete();
          }, () => this.saveComplete());

        } else {
          if (this.saveComplete) {
            this.saveComplete();
          }
          this.saveDone.emit(true);
          // USTAS SEM-6081 - в выпдающем календаре не было перехода на контроллеры (спорный пункт - на тестирование)
          // if (!this.disableRoute && this.isRouteToControllers) {
          if (this.isRouteToControllers) {
            this.router.navigate(['main', 'page', 'object', this.ids[0], 'controller']);
          }
        }

      });

    this.hideDialog();
  }

  /**
   * Добавляет дату переноса выходного дня, дополнительно к производственному календарю
   */
  addObjectHoliday(): void {
    const now = new Date();
    this.objectHolidays.push({
      date: new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime(),
      operatingmode: {weekday: 1}
    });
  }

  /**
   * Устанавливает шаблон для модального окна и отображает модальное окно
   * @param dialogTemplate - cсылка на шаблон отображения в модальном окне
   */
  confirmSetAction(dialogTemplate) {
    this.modalDialogTemplate = dialogTemplate;
    this.showModalDialog = true;
  }

  /**
   * Убирает перенос выходного дня
   */
  removeObjectHoliday(id): void {
    this.objectHolidays = this.objectHolidays.filter((el, idx) => idx !== id);
  }

  /**
   * Скрывает диалог
   */
  hideDialog() {
    this.showModalDialog = false;
  }

  /**
   * Возвращает текущую локаль
   * @returns {string}
   */
  public locale() {
    return this.translate.currentLang;
  }

  public validateForm() {
    this.isValid = true;
    if (this.objectHolidays && this.objectHolidays.length > 1) {
      for (let i = 0; i < this.objectHolidays.length; i++) {
          const holiday = new Date(this.objectHolidays[i].date);
          if (this.objectHolidays.filter(hol => {
            const dt = new Date(hol.date);
            return dt.getFullYear() === holiday.getFullYear() && dt.getMonth() === holiday.getMonth() && dt.getDate() === holiday.getDate();
          }).length > 1) {
            this.isValid = false;
            break;
          }
        }
    }
    return !this.isValid;
  }
}
